import { useContext, useCallback, useMemo } from 'react';
import { FilterContext } from '../../components/GlobalFilterProvider/GlobalFilterProvider.esm.js';
import { actions } from '../../state/actions.esm.js';
import { defaultFilterState } from '../../state/reducer.esm.js';

/**
 * A hook that provides the filters and a function to set the filters for a
 * given filter name. If the hook is used within a FilterProvider, it will
 * use the name given to FilterProvider, otherwise you must pass the name
 * of the FilterBar to useFilter.
 */
const useFilters = (name) => {
    const { dispatch, state, name: nameContext } = useContext(FilterContext);
    const resolvedName = name || nameContext;
    if (!resolvedName)
        throw "useFilters: A name must be provided to useFilters or by using FilterProvider";
    const setFilters = useCallback((newFilters) => {
        dispatch({
            type: actions.mergeFilters,
            payload: { name: resolvedName, filters: newFilters },
        });
    }, [resolvedName]);
    const replaceFilters = useCallback((newFilters) => {
        dispatch({
            type: actions.replaceFilters,
            payload: { name: resolvedName, filters: newFilters },
        });
    }, [resolvedName]);
    const replaceDisplayedFilters = useCallback((newFilters) => {
        dispatch({
            type: actions.replaceDisplayedFilters,
            payload: { name: resolvedName, filters: newFilters },
        });
    }, [resolvedName]);
    const resetFilters = useCallback(() => {
        dispatch({
            type: actions.resetFilters,
            payload: { name: resolvedName },
        });
    }, [resolvedName]);
    return useMemo(() => {
        const currentState = { ...defaultFilterState, ...state[resolvedName] };
        return {
            ...currentState,
            name: resolvedName,
            dispatch,
            replaceFilters,
            replaceDisplayedFilters,
            resetFilters,
            setFilters,
            state: {
                filters: currentState.filters,
                displayedFilters: currentState.displayedFilters,
                defaultFilters: currentState.defaultFilters,
            },
        };
    }, [
        resolvedName,
        dispatch,
        setFilters,
        replaceFilters,
        replaceDisplayedFilters,
        resetFilters,
        state[resolvedName],
    ]);
};

export { useFilters };
//# sourceMappingURL=useFilters.esm.js.map
